/**
 * Sales invoices or purchase invoices: the list, what each still owes, and
 * "record payment", which opens a voucher already pointed at the invoice.
 */

import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import { Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import CargoLoader from "components/CargoLoader";
import FilterBar, { filterRows } from "components/FilterBar";
import { FilterSelect, FilterDates, matches, inDateRange } from "components/FilterBar/controls";
import Pagination, { usePagination } from "components/Pagination";
import RowActions from "components/RowActions";

import InvoiceDialog from "./InvoiceDialog";
import VoucherDialog from "./VoucherDialog";
import { printInvoice } from "./print";
import { api, problemOf, money, docNo, byCurrency, currencyLine, LOOKUPS } from "./api";

const cell = { fontSize: 14, whiteSpace: "nowrap" };

const STATUS = {
  unpaid: { label: "Unpaid", tone: "error" },
  partial: { label: "Part paid", tone: "warning" },
  paid: { label: "Paid", tone: "success" },
};

function InvoicesTab({ type, lookups, onChanged }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  // {id} to edit, {} for new, null when closed
  const [editing, setEditing] = useState(null);
  // the invoice a payment is being recorded against
  const [paying, setPaying] = useState(null);

  const sales = type === "sales";

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await api.get("/invoices", { type });
      setRows(
        (Array.isArray(data) ? data : []).map((i) => ({ ...i, no: docNo(i.type, i.number) }))
      );
    } catch (e) {
      setError(problemOf(e, "Could not load invoices"));
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, [type]);

  const saved = () => {
    load();
    onChanged();
  };

  const print = async (inv) => {
    try {
      printInvoice(await api.get(`/invoices/${inv.id}`));
    } catch (e) {
      // eslint-disable-next-line no-alert
      window.alert(problemOf(e, "Could not load the invoice"));
    }
  };

  const remove = async (inv) => {
    const note =
      inv.paid > 0 ? "\nThe vouchers that paid it stay, unlinked - the money still moved." : "";
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete ${inv.no} (${money(inv.total)} ${inv.currency})?${note}`)) return;
    try {
      await api.del(`/invoices/${inv.id}`);
      saved();
    } catch (e) {
      // eslint-disable-next-line no-alert
      window.alert(problemOf(e, "Could not delete"));
    }
  };

  const narrowed = rows.filter((r) => inDateRange(r.date, from, to) && matches(r.status, status));
  const filtered = filterRows(narrowed, query);
  const pager = usePagination(filtered);

  return (
    <>
      <FilterBar
        title={sales ? "Sales Invoices · فواتير المبيعات" : "Purchase Invoices · فواتير المشتريات"}
        value={query}
        onChange={setQuery}
        count={filtered.length}
        total={rows.length}
        placeholder="Search number, name, reference..."
        actions={
          <>
            <SuiTypography variant="caption" sx={{ opacity: 0.75, whiteSpace: "nowrap" }}>
              {`${sales ? "Receivable" : "Payable"} ${currencyLine(
                byCurrency(filtered, (r) => r.remaining)
              )}`}
            </SuiTypography>
            <Button variant="contained" color="primary" onClick={() => setEditing({})}>
              New Invoice
            </Button>
          </>
        }
      >
        <FilterSelect
          label="Status"
          icon="flag"
          value={status}
          onChange={setStatus}
          options={Object.entries(STATUS).map(([value, s]) => ({ value, label: s.label }))}
        />
        <FilterDates label="Date" from={from} to={to} onFrom={setFrom} onTo={setTo} />
      </FilterBar>

      {error && (
        <SuiBox px={3} pb={2}>
          <SuiTypography variant="button" color="error">
            {error}
          </SuiTypography>
        </SuiBox>
      )}

      {loading ? (
        <CargoLoader label="Loading invoices" />
      ) : (
        <>
          <SuiBox sx={{ overflowX: "auto" }}>
            <Table sx={{ minWidth: 1100 }}>
              <TableHead>
                <TableRow>
                  <TableCell align="center">No.</TableCell>
                  <TableCell align="center">Date</TableCell>
                  <TableCell align="center">{sales ? "Customer" : "Supplier"}</TableCell>
                  <TableCell align="center">Reference</TableCell>
                  <TableCell align="center">Due</TableCell>
                  <TableCell align="center">Total</TableCell>
                  <TableCell align="center">Paid</TableCell>
                  <TableCell align="center">Remaining</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Operations</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pager.pageRows.map((inv) => {
                  const st = STATUS[inv.status] || STATUS.unpaid;
                  return (
                    <TableRow key={inv.id}>
                      <TableCell align="center" sx={{ ...cell, fontWeight: 700 }}>
                        {inv.no}
                      </TableCell>
                      <TableCell align="center" sx={cell}>
                        {inv.date}
                      </TableCell>
                      <TableCell align="center" sx={cell}>
                        {inv.party_name || "-"}
                      </TableCell>
                      <TableCell align="center" sx={cell}>
                        {inv.reference || "-"}
                      </TableCell>
                      <TableCell align="center" sx={cell}>
                        {inv.due_date || "-"}
                      </TableCell>
                      <TableCell align="center" sx={{ ...cell, fontWeight: 700 }}>
                        {`${money(inv.total)} ${inv.currency}`}
                      </TableCell>
                      <TableCell align="center" sx={cell}>
                        {money(inv.paid)}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={({ palette }) => ({
                          ...cell,
                          fontWeight: 700,
                          color: inv.remaining > 0 ? palette.error.main : palette.success.main,
                        })}
                      >
                        {money(inv.remaining)}
                      </TableCell>
                      <TableCell align="center" sx={cell}>
                        <Chip
                          size="small"
                          label={st.label}
                          sx={({ palette, functions: { rgba } }) => ({
                            fontWeight: 700,
                            color: palette[st.tone].main,
                            backgroundColor: rgba(palette[st.tone].main, 0.12),
                          })}
                        />
                      </TableCell>
                      <TableCell align="center" sx={cell}>
                        <RowActions
                          actions={[
                            inv.remaining > 0 && {
                              icon: sales ? "call_received" : "call_made",
                              label: sales ? "Record receipt" : "Record payment",
                              tone: "primary",
                              onClick: () => setPaying(inv),
                            },
                            {
                              icon: "print",
                              label: "Print",
                              need: "print",
                              tone: "info",
                              onClick: () => print(inv),
                            },
                            {
                              icon: "edit",
                              label: "Edit",
                              onClick: () => setEditing({ id: inv.id }),
                            },
                            {
                              icon: "delete",
                              label: "Delete",
                              tone: "danger",
                              onClick: () => remove(inv),
                            },
                          ]}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
                {filtered.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                      <SuiTypography variant="button" color="text">
                        No invoices yet
                      </SuiTypography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </SuiBox>
          <Pagination {...pager} />
        </>
      )}

      <InvoiceDialog
        open={Boolean(editing)}
        type={type}
        invoiceId={editing?.id || null}
        lookups={lookups}
        onClose={() => setEditing(null)}
        onSaved={saved}
      />

      <VoucherDialog
        open={Boolean(paying)}
        type={sales ? "receipt" : "payment"}
        invoice={paying}
        lookups={lookups}
        onClose={() => setPaying(null)}
        onSaved={saved}
      />
    </>
  );
}

InvoicesTab.propTypes = {
  type: PropTypes.oneOf(["sales", "purchase"]).isRequired,
  lookups: LOOKUPS.isRequired,
  onChanged: PropTypes.func.isRequired,
};

export default InvoicesTab;
